import { useRef, useCallback, useEffect } from "react";
import { idbGet, idbKeys, idbPut } from "@utils/idb";

// Constants
const MAX_SEGMENT_MS = 10 * 60 * 1000;
const CHUNK_TIMESLICE_MS = 1000;
const OVERLAP_MS = 800;
const MIME = "audio/webm;codecs=opus";

export const useRecordingSessions = () => {
  const sessionsRef = useRef(new Map());
  const currentSessionIdRef = useRef(null);
  const ensureSessionManifest = useCallback(async (sessionId, createdAtISO) => {
    const manifest = await idbGet("sessions", sessionId);
    if (manifest) return;
      await idbPut("sessions", sessionId, {
        sessionId,
        createdAtISO: createdAtISO || new Date().toISOString(),
        segments: [],
        transcriptionParts: [],
        status: "awaiting_upload",
        uploadingStarted: false,
        completed: false,
        emergencyFlush: false,
        nextIndex: 0,
      });
    }, []);

  const ensureSession = useCallback(
    async (sessionId, createdAtISO) => {
      await ensureSessionManifest(sessionId, createdAtISO);
      if (!sessionsRef.current.has(sessionId)) {
        sessionsRef.current.set(sessionId, {
          stream: null,
          recorders: new Set(),
          buffers: new Map(),
          rotating: new Set(),
          transcriptionParts: [],
          pendingUploads: Promise.resolve(),
          uploadingStarted: false,
          resetPending: false,
          skipUpload: false,
          createdAtISO: createdAtISO || new Date().toISOString(),
          nextIndex: 0,
          completed: false,
        });
      }
      currentSessionIdRef.current = sessionId;
      return sessionsRef.current.get(sessionId);
    },
    [ensureSessionManifest]
  );

  const hasSession = useCallback((sessionId) => {
    return sessionsRef.current.has(sessionId);
  }, []);
  useEffect(() => {
    const loadPersistedSessions = async () => {
      try {
        const sessionIds = await idbKeys("sessions");
        console.log("Found persisted sessions:", sessionIds);

        for (const sessionId of sessionIds) {
          const manifest = await idbGet("sessions", sessionId);
          if (!manifest) continue;

          if (!manifest.completed) {
            console.log("Recovering session:", sessionId, manifest);

            if (!currentSessionIdRef.current) {
              currentSessionIdRef.current = sessionId;
            }
            // Rehydrate session
            const session = {
              stream: null, // Can't restore stream, but we can restore data
              recorders: new Set(),
              buffers: new Map(),
              rotating: new Set(),
              transcriptionParts: manifest.transcriptionParts || [],
              pendingUploads: Promise.resolve(),
              uploadingStarted: manifest.uploadingStarted || false,
              resetPending: false,
              skipUpload: false,
              createdAtISO: manifest.createdAtISO,
              nextIndex: manifest.nextIndex || 0,
              emergencyFlush: manifest.emergencyFlush || false,
              recovered: true, // Mark as recovered
            };
            sessionsRef.current.set(sessionId, session);
            if (!currentSessionIdRef.current) {
              currentSessionIdRef.current = sessionId;
            }
          }
        }
      } catch (error) {
        console.error("Failed to load persisted sessions:", error);
      }
    };

    loadPersistedSessions();
  }, []);

  const createSession = useCallback((stream) => {
    const sessionId =
      crypto.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const session = {
      stream,
      recorders: new Set(),
      buffers: new Map(),
      rotating: new Set(),
      transcriptionParts: [],
      pendingUploads: Promise.resolve(),
      uploadingStarted: false,
      resetPending: false,
      skipUpload: false,
      createdAtISO: new Date().toISOString(),
      nextIndex: 0,
      completed: false,
    };

    sessionsRef.current.set(sessionId, session);
    currentSessionIdRef.current = sessionId;

    return { sessionId, session };
  }, []);

  const getCurrentSession = useCallback(() => {
    const sessionId = currentSessionIdRef.current;
    return sessionId ? sessionsRef.current.get(sessionId) : null;
  }, []);

  const getSession = useCallback((sessionId) => {
    return sessionsRef.current.get(sessionId);
  }, []);

  const deleteSession = useCallback((sessionId) => {
    sessionsRef.current.delete(sessionId);
    if (currentSessionIdRef.current === sessionId) {
      currentSessionIdRef.current = null;
    }
  }, []);

  const isCurrent = useCallback((sessionId) => {
    return currentSessionIdRef.current === sessionId;
  }, []);

  return {
    sessionsRef,
    currentSessionIdRef,
    createSession,
    getCurrentSession,
    getSession,
    deleteSession,
    isCurrent,
    constants: { MAX_SEGMENT_MS, CHUNK_TIMESLICE_MS, OVERLAP_MS, MIME },
    ensureSession,
    hasSession,
  };
};